import React from "react";
import { motion } from "framer-motion";
import {
  CalendarDays,
  House,
  PlusCircle,
  BarChart3,
  User,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import telegramService from "../utils/telegram";

interface BottomNavigationProps {
  activeTab: string;
  onTabChange: (tab: string) => void;
}

const navItems = [
  { id: "home", labelKey: "nav.home", icon: House },
  { id: "plan", labelKey: "nav.plan", icon: CalendarDays },
  { id: "log", labelKey: "nav.addFood", icon: PlusCircle, isCenter: true },
  { id: "stats", labelKey: "nav.stats", icon: BarChart3 },
  { id: "profile", labelKey: "nav.profile", icon: User },
];

const BottomNavigation: React.FC<BottomNavigationProps> = ({
  activeTab,
  onTabChange,
}) => {
  const { t } = useTranslation();

  const handleTabClick = (tab: string) => {
    telegramService.haptic(tab === "log" ? "medium" : "selection");
    onTabChange(tab);
  };

  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 pointer-events-none">
      <div className="mx-auto max-w-md px-3 pointer-events-auto pb-[calc(0.75rem+var(--safe-bottom))]">
        <div className="bg-surface-elevated backdrop-blur-2xl border border-line shadow-elevated rounded-[1.75rem] px-2 py-2 flex justify-around items-center">
          {navItems.map((item) => {
            const isActive = activeTab === item.id;
            const Icon = item.icon;

            if (item.isCenter) {
              return (
                <button
                  key={item.id}
                  onClick={() => handleTabClick(item.id)}
                  className="relative -top-5 rounded-full w-14 h-14 flex items-center justify-center bg-content-primary shadow-elevated text-content-inverse active:scale-95 transition-transform duration-150 ring-4 ring-surface"
                  aria-label={t(item.labelKey)}
                >
                  <Icon size={26} strokeWidth={2.5} />
                </button>
              );
            }

            return (
              <button
                key={item.id}
                onClick={() => handleTabClick(item.id)}
                className="relative flex flex-col items-center gap-0.5 min-w-[56px] py-1.5 rounded-2xl transition-colors duration-200"
                aria-label={t(item.labelKey)}
              >
                <div className="relative">
                  <Icon
                    size={22}
                    strokeWidth={isActive ? 2.5 : 1.8}
                    className={
                      isActive ? "text-content-primary" : "text-content-tertiary"
                    }
                  />
                </div>
                <span
                  className={`text-[10px] font-semibold ${
                    isActive ? "text-content-primary" : "text-content-tertiary"
                  }`}
                >
                  {t(item.labelKey)}
                </span>
                {isActive && (
                  <motion.div
                    layoutId="nav-indicator"
                    className="absolute -top-0.5 w-7 h-1 bg-content-primary rounded-full"
                    transition={{ type: "spring", stiffness: 400, damping: 30 }}
                  />
                )}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default BottomNavigation;
